import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useAgreementDrivers,
  useCreateDriver,
  usePatchDriver,
  useDeleteDriver,
  type AgreementDriver,
  type DriverBody,
} from '../../hooks/useAgreements';

interface DriverManagerProps {
  avtaleId: string;
  canEdit: boolean;
  beloepGjeldende?: number | null;
}

interface DriverForm {
  aar: string;
  navn: string;
  betaler: string;
  enhet: string;
  volum: string;
  enhetspris: string;
  paaslagProsent: string;
  fastledd: string;
  beregningsregel: string;
  kilde: string;
  notat: string;
}

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('nb-NO', { maximumFractionDigits: 2 }) + ' kr';
}

function num(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Live preview-formel — speiler backend `beregnDriverBeloep`. */
export function beregnDriverBeloepPreview(input: {
  volum: number | null;
  enhetspris: number | null;
  paaslagProsent: number | null;
  fastledd: number | null;
}): number {
  const volum = input.volum ?? 0;
  const enhetspris = input.enhetspris ?? 0;
  const paaslag = input.paaslagProsent ?? 0;
  const fastledd = input.fastledd ?? 0;
  return Math.round((volum * enhetspris * (1 + paaslag / 100) + fastledd) * 100) / 100;
}

const EMPTY_FORM: DriverForm = {
  aar: String(new Date().getFullYear()),
  navn: '',
  betaler: '',
  enhet: '',
  volum: '',
  enhetspris: '',
  paaslagProsent: '',
  fastledd: '',
  beregningsregel: '',
  kilde: '',
  notat: '',
};

function driverToForm(d: AgreementDriver): DriverForm {
  return {
    aar: String(d.Aar),
    navn: d.Navn,
    betaler: d.Betaler ?? '',
    enhet: d.Enhet ?? '',
    volum: d.Volum == null ? '' : String(d.Volum),
    enhetspris: d.Enhetspris == null ? '' : String(d.Enhetspris),
    paaslagProsent: d.Paaslag_prosent == null ? '' : String(d.Paaslag_prosent),
    fastledd: d.Fastledd == null ? '' : String(d.Fastledd),
    beregningsregel: d.Beregningsregel ?? '',
    kilde: d.Kilde ?? '',
    notat: d.Notat ?? '',
  };
}

function formToBody(f: DriverForm): DriverBody {
  return {
    aar: num(f.aar) ?? undefined,
    navn: f.navn.trim(),
    betaler: f.betaler.trim() || null,
    enhet: f.enhet.trim() || null,
    volum: num(f.volum),
    enhetspris: num(f.enhetspris),
    paaslagProsent: num(f.paaslagProsent),
    fastledd: num(f.fastledd),
    beregningsregel: f.beregningsregel.trim() || null,
    kilde: f.kilde.trim() || null,
    notat: f.notat.trim() || null,
  };
}

export function DriverManager({ avtaleId, canEdit, beloepGjeldende }: DriverManagerProps) {
  const { data, isLoading, error } = useAgreementDrivers(avtaleId);
  const createDriver = useCreateDriver(avtaleId);
  const patchDriver = usePatchDriver(avtaleId);
  const deleteDriver = useDeleteDriver(avtaleId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AgreementDriver | null>(null);
  const [form, setForm] = useState<DriverForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgreementDriver | null>(null);

  const drivers = data?.data ?? [];

  // Grupper per år (synkende), med sum per år
  const yearGroups = useMemo(() => {
    const byYear = new Map<number, AgreementDriver[]>();
    for (const d of drivers) {
      const arr = byYear.get(d.Aar) ?? [];
      arr.push(d);
      byYear.set(d.Aar, arr);
    }
    return Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([aar, rows]) => ({
        aar,
        rows: rows.slice().sort((a, b) => a.Navn.localeCompare(b.Navn, 'nb')),
        sum: rows.reduce((s, r) => s + (r.Beregnet_beloep ?? 0), 0),
      }));
  }, [drivers]);

  const previewBeloep = beregnDriverBeloepPreview({
    volum: num(form.volum),
    enhetspris: num(form.enhetspris),
    paaslagProsent: num(form.paaslagProsent),
    fastledd: num(form.fastledd),
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(d: AgreementDriver) {
    setEditing(d);
    setForm(driverToForm(d));
    setFormError(null);
    setDialogOpen(true);
  }

  function setField(key: keyof DriverForm, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    const aar = num(form.aar);
    if (aar === null || !Number.isInteger(aar) || aar < 2000 || aar > 2100) {
      setFormError('År må være et heltall mellom 2000 og 2100.');
      return;
    }
    if (form.navn.trim() === '') {
      setFormError('Navn er påkrevd.');
      return;
    }
    setFormError(null);
    const body = formToBody(form);
    try {
      if (editing) {
        await patchDriver.mutateAsync({ driverId: editing.DriverID, body });
      } else {
        await createDriver.mutateAsync(body);
      }
      setDialogOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Lagring feilet.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteDriver.mutateAsync(deleteTarget.DriverID);
      setDeleteTarget(null);
    } catch {
      // feilen vises via mutation-state; behold dialogen
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Kunne ikke laste drivere: {error instanceof Error ? error.message : 'Ukjent feil'}</Alert>;
  }

  const isSaving = createDriver.isPending || patchDriver.isPending;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Drivere / beregning</Typography>
          <Typography variant="body2" color="text.secondary">
            For avtaler uten fast sum: beløp = volum × enhetspris × (1 + påslag%) + fastledd.
            Nyeste års sum styrer gjeldende beløp ({formatCurrency(beloepGjeldende)}).
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} data-testid="add-driver">
            Legg til driver
          </Button>
        )}
      </Box>

      {drivers.length === 0 ? (
        <Alert severity="info" data-testid="drivers-empty">
          Ingen drivere registrert. Bruk drivere for inntekter der beløpet beregnes (f.eks. innbyggertall × kostnad + påslag).
        </Alert>
      ) : (
        <Stack spacing={3}>
          {yearGroups.map((g) => (
            <Box key={g.aar} data-testid={`driver-year-${g.aar}`}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                År {g.aar}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Navn</TableCell>
                    <TableCell>Betaler</TableCell>
                    <TableCell>Enhet</TableCell>
                    <TableCell align="right">Volum</TableCell>
                    <TableCell align="right">Enhetspris</TableCell>
                    <TableCell align="right">Påslag %</TableCell>
                    <TableCell align="right">Fastledd</TableCell>
                    <TableCell align="right">Beregnet</TableCell>
                    {canEdit && <TableCell align="right">Handling</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.rows.map((d) => (
                    <TableRow key={d.DriverID} data-testid="driver-row">
                      <TableCell>{d.Navn}</TableCell>
                      <TableCell>{d.Betaler ?? '—'}</TableCell>
                      <TableCell>{d.Enhet ?? '—'}</TableCell>
                      <TableCell align="right">{d.Volum ?? '—'}</TableCell>
                      <TableCell align="right">{d.Enhetspris ?? '—'}</TableCell>
                      <TableCell align="right">{d.Paaslag_prosent ?? '—'}</TableCell>
                      <TableCell align="right">{d.Fastledd ?? '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(d.Beregnet_beloep)}</TableCell>
                      {canEdit && (
                        <TableCell align="right">
                          <Tooltip title="Rediger">
                            <IconButton size="small" onClick={() => openEdit(d)} aria-label="rediger driver">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Slett">
                            <IconButton size="small" onClick={() => setDeleteTarget(d)} aria-label="slett driver">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ fontWeight: 600 }}>
                      Sum {g.aar}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }} data-testid={`driver-sum-${g.aar}`}>
                      {formatCurrency(g.sum)}
                    </TableCell>
                    {canEdit && <TableCell />}
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          ))}
        </Stack>
      )}

      {/* Opprett/rediger-dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Rediger driver' : 'Legg til driver'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField
                label="År" required type="number" value={form.aar}
                onChange={(e) => setField('aar', e.target.value)} sx={{ width: 120 }}
                inputProps={{ 'data-testid': 'driver-aar' }}
              />
              <TextField
                label="Navn" required fullWidth value={form.navn}
                onChange={(e) => setField('navn', e.target.value)}
                inputProps={{ 'data-testid': 'driver-navn' }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Betaler" fullWidth value={form.betaler} onChange={(e) => setField('betaler', e.target.value)} />
              <TextField label="Enhet" value={form.enhet} onChange={(e) => setField('enhet', e.target.value)} sx={{ width: 160 }} placeholder="innbygger / time / m2" InputLabelProps={{ shrink: true }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Volum" fullWidth value={form.volum} onChange={(e) => setField('volum', e.target.value)} inputProps={{ 'data-testid': 'driver-volum' }} />
              <TextField label="Enhetspris" fullWidth value={form.enhetspris} onChange={(e) => setField('enhetspris', e.target.value)} inputProps={{ 'data-testid': 'driver-enhetspris' }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Påslag %" fullWidth value={form.paaslagProsent} onChange={(e) => setField('paaslagProsent', e.target.value)} />
              <TextField label="Fastledd" fullWidth value={form.fastledd} onChange={(e) => setField('fastledd', e.target.value)} />
            </Stack>
            <TextField label="Beregningsregel" fullWidth value={form.beregningsregel} onChange={(e) => setField('beregningsregel', e.target.value)} placeholder="Lesbar formel / kildehenvisning" InputLabelProps={{ shrink: true }} />
            <Stack direction="row" spacing={2}>
              <TextField label="Kilde" fullWidth value={form.kilde} onChange={(e) => setField('kilde', e.target.value)} placeholder="Budsjett 2025 / Avregning" InputLabelProps={{ shrink: true }} />
              <TextField label="Notat" fullWidth value={form.notat} onChange={(e) => setField('notat', e.target.value)} />
            </Stack>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="body2" color="text.secondary">Beregnet beløp (forhåndsvisning)</Typography>
              <Typography variant="h6" data-testid="driver-preview">{formatCurrency(previewBeloep)}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving} data-testid="driver-save">
            {isSaving ? 'Lagrer…' : 'Lagre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slett-bekreftelse */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Slette driver?</DialogTitle>
        <DialogContent>
          <Typography>Vil du slette driveren «{deleteTarget?.Navn}» ({deleteTarget?.Aar})?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteDriver.isPending} data-testid="driver-delete-confirm">
            Slett
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
